import { isStagingTestMode } from '../lib/stagingEnvironment'
import React, { useRef, useState } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/AuthContext'

export default function Layout() {
  const { signOut, workshop, isSuperAdmin, profile, accessLevel, accessEndsAt } = useAuth()
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const [failedLogoUrl, setFailedLogoUrl] = useState('')
  const menuButtonRef = useRef(null)

  async function handleSignOut() {
    await signOut()
    navigate('/login')
  }

  function closeMenuAfterNavigation() {
    if (!menuOpen) return
    setMenuOpen(false)
    window.requestAnimationFrame(() => menuButtonRef.current?.focus())
  }

  const workshopName = workshop?.name || 'Workshop'
  const logoUrl = workshop?.logo_url || ''
  const localTestMode = process.env.REACT_APP_LOCAL_TEST_MODE === 'true'

  return (
    <div className="app-shell">
      <aside id="app-sidebar" className={`app-sidebar ${menuOpen ? 'is-open' : ''}`}>
        <div className="app-brand-block">
          <div className="app-brand">
            <img src="/rebound-logo-white-ink-transparent.png" alt="Rebound" />
          </div>
        </div>

        <nav className="app-primary-nav" aria-label="Workspace">
          <p>Workspace</p>
          <NavLink to="/" end onClick={closeMenuAfterNavigation}>Workshop</NavLink>
          <NavLink to="/calendar" onClick={closeMenuAfterNavigation}>Calendar</NavLink>
          <NavLink to="/jobs" onClick={closeMenuAfterNavigation}>Jobs</NavLink>
          <NavLink to="/customers" onClick={closeMenuAfterNavigation}>Customers</NavLink>
          <NavLink to="/payments" onClick={closeMenuAfterNavigation}>Payments</NavLink>
          {profile?.role === 'owner' && profile.workshop_id === workshop?.id && <NavLink to="/reporting" onClick={closeMenuAfterNavigation}>Reporting</NavLink>}
          {profile?.role === 'owner' && profile.workshop_id === workshop?.id && <NavLink to="/financial-records" onClick={closeMenuAfterNavigation}>Financial records</NavLink>}
        </nav>

        <div className="app-sidebar-bottom">
          <nav className="app-support-nav" aria-label="Workshop tools">
            <NavLink to="/tablet" onClick={closeMenuAfterNavigation}>Bench mode</NavLink>
            <NavLink to="/settings" onClick={closeMenuAfterNavigation}>Workshop setup</NavLink>
          </nav>
          {isSuperAdmin && (
            <NavLink to="/admin" className="app-admin-link" onClick={closeMenuAfterNavigation}>Founder admin</NavLink>
          )}
          <div className="app-person">
            <span>{(workshopName || 'WS').slice(0, 2).toUpperCase()}</span>
            <div><strong>{workshopName}</strong><button type="button" onClick={handleSignOut}>Sign out</button></div>
          </div>
        </div>
      </aside>

      <div className="app-main">
        <header className="app-topbar">
          <button ref={menuButtonRef} className="app-menu-button" onClick={() => setMenuOpen(open => !open)} aria-expanded={menuOpen} aria-controls="app-sidebar">{menuOpen ? 'Close' : 'Menu'}</button>
          <div className="app-topbar-context">
            {logoUrl && logoUrl !== failedLogoUrl ? (
              <img className="app-workshop-logo" src={logoUrl} alt={`${workshopName} logo`} onError={() => setFailedLogoUrl(logoUrl)} />
            ) : (
              <span>{workshopName}</span>
            )}
          </div>
          {localTestMode && <span className="app-local-flag">Local test · no production writes</span>}
          {isStagingTestMode() && <span className="app-local-flag">Hosted test · fictional records</span>}
        </header>

        <main className="app-content" onClick={() => menuOpen && setMenuOpen(false)}>
          {accessLevel === 'read_only' && (
            <div className="alert alert-warning app-read-only-banner" role="status">
              <span>
                Your Rebound subscription has ended, so this workshop is read-only
                {accessEndsAt ? ` until ${accessEndsAt.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}` : ''}.
                {' '}You can view and export records but can&apos;t make changes.
              </span>
              {profile?.role === 'owner' && <NavLink to="/subscribe" className="btn btn-primary btn-sm">Reactivate</NavLink>}
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  )
}
